// pages/formularios/FormulariosPage.js — página /formularios (WhatsApp Flows).
//
// Out/26, PR 1 de 3. Mesmo desenho do Meta Business (AdminMeta): título,
// contagens, e as abas — "Criar formulário" (Construtor), "Ver e editar
// formulários" (ListaFormularios) e, desde o PR 2, "Respostas"
// (RespostasFormularios). Só supervisor e admin (o menu nem mostra pro
// agente, e a API recusa); o agente envia pelo chat.
//
// Tema claro/escuro: a mesma escolha do chat e da Agenda (`cetto:chat-theme`);
// os tokens `--fm-*` ficam em Formularios.css.

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Tab, Tabs, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import { FiCheckCircle, FiFileText, FiInbox, FiList } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import AppSidebar from '../../components/AppSidebar';
import BloqueioTeste from '../../components/assinatura/BloqueioTeste';
import TopBar from '../../components/TopBar';
import api from '../../services/Api';
import Construtor from './Construtor';
import ListaFormularios from './ListaFormularios';
import RespostasFormularios from './RespostasFormularios';
import { SYNE, botaoPrincipal, botaoTexto, dialogoPapel, selo } from './estilo';
import './Formularios.css';

const CHAVE_TEMA = 'cetto:chat-theme';

function erroDaApi(err, padrao) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  return d?.mensagem || padrao;
}

export default function FormulariosPage() {
  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } }, []);
  const gestor = ['SUPERVISOR', 'ADMIN'].includes(user?.role);
  const theme = useTheme();
  const navigate = useNavigate();
  // No celular os dois rótulos inteiros não cabem e a segunda aba ficava
  // escondida atrás da seta de rolagem.
  const celular = useMediaQuery(theme.breakpoints.down('sm'));

  const [tema, setTema] = useState(() => {
    try { return localStorage.getItem(CHAVE_TEMA) === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
  });
  useLayoutEffect(() => {
    const el = document.documentElement;
    if (tema === 'dark') el.setAttribute('data-theme', 'dark'); else el.removeAttribute('data-theme');
    return () => el.removeAttribute('data-theme');
  }, [tema]);
  const trocarTema = useCallback(() => setTema((t) => {
    const n = t === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(CHAVE_TEMA, n); } catch { /* storage indisponível */ }
    return n;
  }), []);

  const [aba, setAba] = useState(0);
  const [msg, setMsg] = useState(null);
  const [chips, setChips] = useState([]);
  const [pastas, setPastas] = useState([]);
  const [carregouBase, setCarregouBase] = useState(false);
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [porStatus, setPorStatus] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [filtroChip, setFiltroChip] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('ALL');
  // O construtor remonta (key) ao começar outro formulário ou abrir um
  // rascunho: o estado dele nasce do `inicial`, nunca é sincronizado.
  const [edicao, setEdicao] = useState({ chave: 0, inicial: null });
  const [confirmar, setConfirmar] = useState(null);
  const [respostasDe, setRespostasDe] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const res = await api.get('/formularios');
      setItens(res.data.items || []);
      setTotal(res.data.total || 0);
      setPorStatus(res.data.por_status || {});
    } catch (err) {
      setMsg({ tipo: 'error', texto: erroDaApi(err, 'Não foi possível carregar os formulários.') });
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (!gestor) return;
    Promise.all([
      api.get('/templates/meta-info').then((r) => (r.data?.devices || []).filter((d) => d.waba_id)).catch(() => []),
      api.get('/folders').then((r) => r.data || []).catch(() => []),
    ]).then(([c, p]) => { setChips(c); setPastas(p); setCarregouBase(true); });
    carregar();
  }, [gestor, carregar]);

  const novo = () => { setEdicao((e) => ({ chave: e.chave + 1, inicial: null })); setAba(0); };
  const abrir = async (f) => {
    try {
      const res = await api.get(`/formularios/${f.id}`);
      setEdicao((e) => ({ chave: e.chave + 1, inicial: res.data }));
      setAba(0);
      setMsg(null);
    } catch (err) {
      setMsg({ tipo: 'error', texto: erroDaApi(err, 'Não foi possível abrir o formulário.') });
    }
  };

  const duplicar = async (f) => {
    try {
      const res = await api.post(`/formularios/${f.id}/duplicar`);
      await carregar();
      setEdicao((e) => ({ chave: e.chave + 1, inicial: res.data }));
      setAba(0);
      setMsg({ tipo: 'success', texto: `Cópia criada como rascunho. Ajuste e publique “${res.data.name}”.` });
    } catch (err) {
      setMsg({ tipo: 'error', texto: erroDaApi(err, 'Não foi possível duplicar.') });
    }
  };

  const excluir = (f) => setConfirmar({
    titulo: `Excluir “${f.name}”?`,
    texto: 'O rascunho sai da lista. Nada foi enviado a clientes.',
    rotulo: 'Excluir rascunho',
    acao: async () => {
      try {
        await api.delete(`/formularios/${f.id}`);
        if (edicao.inicial?.id === f.id) novo();
        setAba(1);
        setMsg({ tipo: 'success', texto: `Rascunho “${f.name}” excluído.` });
        carregar();
      } catch (err) {
        setMsg({ tipo: 'error', texto: erroDaApi(err, 'Não foi possível excluir.') });
      }
    },
  });

  const descontinuar = (f) => setConfirmar({
    titulo: `Descontinuar “${f.name}”?`,
    texto: 'Não tem volta. O formulário para de abrir para os clientes, inclusive em mensagens já enviadas e nos templates com o botão dele. Para usar de novo, duplique e publique a cópia.',
    rotulo: 'Descontinuar',
    acao: async () => {
      try {
        await api.post(`/formularios/${f.id}/descontinuar`);
        setMsg({ tipo: 'success', texto: `Formulário “${f.name}” descontinuado.` });
        carregar();
      } catch (err) {
        setMsg({ tipo: 'error', texto: erroDaApi(err, 'Não foi possível descontinuar.') });
      }
    },
  });

  const atualizar = async () => {
    setAtualizando(true);
    try {
      const res = await api.post('/formularios/sincronizar', { device_id: filtroChip || null });
      await carregar();
      const falhas = res.data?.falhas || [];
      if (falhas.length) setMsg({ tipo: 'warning', texto: `Não deu para ler a Meta agora: ${falhas[0].mensagem}` });
      else setMsg({ tipo: 'success', texto: res.data?.atualizados ? `${res.data.atualizados} formulário(s) com status novo da Meta.` : 'Tudo em dia com a Meta.' });
    } catch (err) {
      setMsg({ tipo: 'error', texto: erroDaApi(err, 'Não foi possível atualizar.') });
    } finally {
      setAtualizando(false);
    }
  };

  // Limitado e bloqueado continuam publicados na Meta; só o status mudou.
  const publicados = (porStatus.PUBLISHED || 0) + (porStatus.THROTTLED || 0) + (porStatus.BLOCKED || 0);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'var(--fm-bg)' }}>
      <AppSidebar user={user} />
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, ml: { xs: 0, md: 'var(--sidebar-width, 260px)' }, transition: 'margin-left 0.25s ease', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <TopBar crumbBase="Gerenciamento" crumbCur="Formulários" themeMode={tema} onToggleTheme={trocarTema} />
        <BloqueioTeste pagina="formularios" sx={{ overflowY: 'auto', p: { xs: 2, sm: 3, md: 6 } }}>
          <Container maxWidth="xl" disableGutters>
            <Box sx={{ mb: 4 }}>
              <Typography component="h1" sx={{ fontFamily: SYNE, fontWeight: 800, fontSize: '1.65rem', letterSpacing: '-0.5px', lineHeight: 1.1, color: 'var(--fm-title)' }}>
                Formulários
              </Typography>
              <Typography sx={{ mt: 1, color: 'var(--fm-body)', fontSize: '1rem' }}>
                Formulários nativos do WhatsApp (Flows): o cliente responde dentro da conversa e as respostas vão para o CRM.
              </Typography>
              {gestor && (
                <Box sx={{ display: 'flex', gap: 1.2, mt: 2, flexWrap: 'wrap' }}>
                  <Box component="span" sx={{ ...selo('gray'), fontSize: '0.72rem', px: 1.2, py: 0.5, borderRadius: '999px' }}>
                    <FiFileText size={12} /> {total} formulário{total === 1 ? '' : 's'}
                  </Box>
                  <Box component="span" sx={{ ...selo(publicados ? 'green' : 'gray'), fontSize: '0.72rem', px: 1.2, py: 0.5, borderRadius: '999px' }}>
                    <FiCheckCircle size={12} /> {publicados} publicado{publicados === 1 ? '' : 's'}
                  </Box>
                </Box>
              )}
            </Box>

            {msg?.texto && (
              <Alert severity={msg.tipo || 'info'} onClose={() => setMsg(null)} sx={{ mb: 3, borderRadius: 3 }}>{msg.texto}</Alert>
            )}

            {!gestor ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                Formulários são montados por supervisores. Quando um formulário for publicado, você poderá enviá-lo pelas conversas.
              </Alert>
            ) : (
              <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid var(--fm-bdr)', overflow: 'hidden', bgcolor: 'var(--fm-paper)', backgroundImage: 'none' }}>
                {/* No celular as três abas dividem a largura, sem ícone: com
                    ícone e rolagem, "Criar" ficava escondida atrás da seta. */}
                <Tabs
                  value={aba} onChange={(_, v) => setAba(v)}
                  variant={celular ? 'fullWidth' : 'scrollable'} scrollButtons={celular ? false : 'auto'}
                  sx={{
                    px: { xs: 0.5, md: 4 }, borderBottom: '1px solid var(--fm-bdr2)',
                    '& .MuiTabs-indicator': { backgroundColor: 'var(--fm-green)', height: 3 },
                    '& .MuiTab-root': {
                      textTransform: 'none', fontFamily: SYNE, fontWeight: 700, fontSize: { xs: '0.86rem', sm: '0.95rem' },
                      color: 'var(--fm-body)', minHeight: 56, minWidth: { xs: 0, sm: 90 }, px: { xs: 1, sm: 2 },
                      '&.Mui-selected': { color: 'var(--fm-title)' },
                    },
                    '& .MuiTabs-scrollButtons': { color: 'var(--fm-body)' },
                  }}
                >
                  <Tab icon={celular ? undefined : <FiFileText size={16} />} iconPosition="start" label={celular ? 'Criar' : 'Criar formulário'} aria-label="Criar formulário" />
                  <Tab icon={celular ? undefined : <FiList size={16} />} iconPosition="start" label={celular ? 'Ver e editar' : 'Ver e editar formulários'} aria-label="Ver e editar formulários" />
                  <Tab icon={celular ? undefined : <FiInbox size={16} />} iconPosition="start" label="Respostas" aria-label="Respostas" />
                </Tabs>
                <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  {aba === 0 && carregouBase && (
                    <Construtor
                      key={edicao.chave}
                      inicial={edicao.inicial}
                      chips={chips}
                      pastas={pastas}
                      onMensagem={setMsg}
                      onNovo={novo}
                      onSalvo={() => carregar()}
                      onPublicado={(f) => {
                        setMsg({ tipo: 'success', texto: `Formulário “${f.name}” publicado na Meta.` });
                        setEdicao((e) => ({ chave: e.chave + 1, inicial: null }));
                        setAba(1);
                        carregar();
                      }}
                    />
                  )}
                  {aba === 1 && (
                    <ListaFormularios
                      itens={itens} chips={chips} carregando={carregando} atualizando={atualizando}
                      filtroChip={filtroChip} filtroStatus={filtroStatus}
                      onFiltroChip={setFiltroChip} onFiltroStatus={setFiltroStatus}
                      onAtualizar={atualizar} onEditar={abrir} onDuplicar={duplicar}
                      onExcluir={excluir} onDescontinuar={descontinuar} onCriar={novo}
                      onRespostas={(f) => { setRespostasDe(f.id); setAba(2); }}
                      onCriarTemplate={(f) => {
                        const qs = new URLSearchParams({ aba: 'criar', formulario: f.id, chip: f.device_id || '', nome: f.name });
                        navigate(`/admin/meta?${qs.toString()}`);
                      }}
                    />
                  )}
                  {aba === 2 && (
                    <RespostasFormularios itens={itens} inicial={respostasDe} onMensagem={setMsg} />
                  )}
                </Box>
              </Paper>
            )}
          </Container>
        </BloqueioTeste>
      </Box>

      <Dialog open={Boolean(confirmar)} onClose={() => setConfirmar(null)} maxWidth="xs" fullWidth PaperProps={dialogoPapel}>
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800 }}>{confirmar?.titulo}</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: '0.9rem', color: 'var(--fm-body)' }}>{confirmar?.texto}</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmar(null)} sx={{ ...botaoTexto, color: 'var(--fm-body)' }}>Cancelar</Button>
          <Button
            variant="contained" disableElevation
            onClick={() => { const a = confirmar.acao; setConfirmar(null); a(); }}
            sx={{ ...botaoPrincipal, bgcolor: 'var(--fm-red)', color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: 'var(--fm-red)', filter: 'brightness(0.92)' } }}
          >
            {confirmar?.rotulo}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
